import { useEffect } from 'react';
import { locationIsKia } from '../../../utils/helpers';
import {
  CarStoreProvider,
  useCarStoreContext,
} from '../../components/fleet-calculator/store/car-store-provider';
import CarDetails from '../../components/fleet-calculator/ui/car-details';
import FleetCard from '../../components/fleet-calculator/ui/card';
import SwitchCar from '../../components/fleet-calculator/ui/switch-car';
import Snippet from '../../components/Snippet';
import Modal from '../../components/ui/Modal';
import PrimaryButton from '../../components/ui/PrimaryButton';
import SecondaryButton from '../../components/ui/SecondaryButton';
import CarExcerpt from './exerct';
import Filter from './filter';
import Legal from './legal';
import PriceTable from './price-table';
import Settings from './settings';

type FleetCalculatorProps = {
  hideControls?: boolean;
  trimCode?: string;
  availableTrims?: string;
  activeTrims?: string;
  compact?: boolean;
};

function FleetCalculatorInternal({
  hideControls,
  activeTrims,
  availableTrims,
  compact,
}: FleetCalculatorProps) {
  const activeCarPair = useCarStoreContext((state) => state.activeCarPair);
  const selectableTrimCodes = useCarStoreContext(
    (state) => state.selectableTrimCodes,
  );
  const activeTrimCodes = useCarStoreContext((state) => state.activeTrimCodes);
  const initializeTrims = useCarStoreContext((state) => state.initializeTrims);
  const isFilterModalOpen = useCarStoreContext(
    (state) => state.isFilterModalOpen,
  );
  const isCompactLayout = useCarStoreContext((state) => state.isCompactLayout);
  const updateState = useCarStoreContext((state) => state.updateState);

  useEffect(() => {
    const active = activeTrims ? activeTrims.split(',') : [];
    const available = availableTrims ? availableTrims.split(',') : [];

    if (compact) {
      updateState({ isCompactLayout: compact });
    }
    initializeTrims(active, available);
  }, [activeTrims, availableTrims, initializeTrims, compact, updateState]);

  return (
    <div>
      {!locationIsKia() && !hideControls ? <Settings /> : null}
      <SwitchCar />
      <CarDetails />

      {isFilterModalOpen && (
        <Modal clickOutside={() => updateState({ isFilterModalOpen: false })}>
          <div className="relative max-w-xl bg-white max-h-[90dvh] lg:max-h-[70dvh] p-4 mx-2 flex flex-col gap-4 overflow-auto">
            <Filter />
            <SecondaryButton
              className="sticky bottom-0 bg-white shadow-sm"
              onClick={() => updateState({ isFilterModalOpen: false })}
            >
              Spara
            </SecondaryButton>
          </div>
        </Modal>
      )}

      <section className={`lg:grid ${!isCompactLayout && 'lg:grid-cols-3'}`}>
        {!isCompactLayout && (
          <div className="hidden pl-12 pr-6 py-10 lg:grid bg-gray-100 col-span-1">
            <Filter />
          </div>
        )}
        <div
          className={`${isCompactLayout ? 'col-span-3' : 'lg:col-span-2'} grid gap-4 px-4 lg:px-8 py-10`}
        >
          <CarExcerpt />
          <div className="grid grid-cols-2 gap-4 lg:gap-8">
            {activeCarPair && activeCarPair.length > 0
              ? activeCarPair.map((car, index) =>
                  car?.car ? (
                    <FleetCard
                      key={car.car.code + index}
                      car={car}
                      index={index}
                    />
                  ) : null,
                )
              : null}
            <PrimaryButton
              onClick={() => updateState({ isFilterModalOpen: true })}
              className={`${!isCompactLayout && 'lg:hidden'} col-span-2`}
            >
              Ändra generalla uppgifter
            </PrimaryButton>
            {activeCarPair && activeCarPair.length > 0 ? <PriceTable /> : null}
          </div>
          <Legal />
        </div>
      </section>

      {activeTrimCodes.length > 0 ? (
        <Snippet
          page="fleet-calculator"
          props={`activeTrims="${activeTrimCodes.join(',')}" availableTrims="${selectableTrimCodes.join(',')}" ${isCompactLayout ? 'compact="true"' : ''}`}
        />
      ) : null}
    </div>
  );
}

export default function FleetCalculator(props: FleetCalculatorProps) {
  return (
    <CarStoreProvider>
      <FleetCalculatorInternal {...props} />
    </CarStoreProvider>
  );
}
